import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import MemberAvatar from '@/components/MemberAvatar';
import {
  Users, MessageSquare, CalendarPlus, Clock, MapPin, ChevronRight,
  User as UserIcon, Sparkles, CalendarDays, CalendarCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const _hhmm = (s) => {
  const parts = String(s || '').split(':');
  return parts.length === 2 ? parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10) : NaN;
};

const _combineDateTime = (date, time) => {
  // Return a Date for sort/compare. Missing time → start of day so items
  // still land on the right day.
  if (!date) return null;
  const safeTime = /^\d{2}:\d{2}$/.test(time || '') ? time : '00:00';
  const d = new Date(`${date}T${safeTime}:00`);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * MyEventSheet — mobile-first "My Event" bottom-sheet.
 *
 * Shows:
 *   • A prominent "Next up" tile pinned to the top (the next registered
 *     session OR accepted meeting, whichever is sooner).
 *   • Quick-action row for Attendees / Messages / Set availability.
 *   • A chronological timeline merging event agenda rows with the
 *     attendee's accepted meetings.
 *   • A small Pending Meetings panel so inbound requests still get
 *     surfaced without being pinned to the top.
 *
 * Meeting rows carry a chevron into the member profile modal and a
 * message icon that deep-links to /messages?to=…&event_id=…
 *
 * Props:
 *   open, onClose
 *   event: { event_id, title, date, ... }
 *   user: current logged-in member
 *   onOpenAttendees:    () => void   — opens the attendee directory
 *   onOpenMessages:     () => void   — jumps to /messages?event_id=
 *   onOpenAvailability: () => void   — opens the availability editor
 *   onOpenProfile:      (attendee) => void   — opens the member profile modal
 *   onMeetingAction:    (requestId, status) => Promise  — accept/decline/cancel
 */
export default function MyEventSheet({
  open, onClose, event, user,
  onOpenAttendees, onOpenMessages, onOpenAvailability,
  onOpenProfile, onMeetingAction,
}) {
  const [schedule, setSchedule] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(false);

  // Shared load so Accept/Decline from inside the sheet refreshes instantly.
  const reload = async (eventId) => {
    if (!eventId) return;
    setLoading(true);
    try {
      const [sRes, mRes] = await Promise.all([
        fetch(`/api/events/${eventId}/my-schedule`, { credentials: 'include' }),
        fetch(`/api/events/${eventId}/meeting-requests`, { credentials: 'include' }),
      ]);
      const s = sRes.ok ? await sRes.json() : { items: [] };
      const m = mRes.ok ? await mRes.json() : [];
      setSchedule(s.items || []);
      setMeetings(m || []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open || !event?.event_id) return;
    reload(event.event_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event?.event_id]);

  // "Next up" — earliest schedule item (session or accepted meeting)
  // whose end time hasn't passed. Falls back to the next future entry,
  // then to any entry if all are in the past.
  const now = useMemo(() => new Date(), []);
  const sortedTimeline = useMemo(() => {
    return [...schedule]
      .map(item => ({ ...item, _start: _combineDateTime(item.date, item.start_time) }))
      .sort((a, b) => (a._start?.getTime() || 0) - (b._start?.getTime() || 0));
  }, [schedule]);

  const nextUp = useMemo(() => {
    if (sortedTimeline.length === 0) return null;
    // Prefer items whose END time is in the future
    const future = sortedTimeline.filter(it => {
      const endDt = _combineDateTime(it.date, it.end_time || it.start_time);
      return endDt && endDt.getTime() >= now.getTime();
    });
    return future[0] || sortedTimeline[0];
  }, [sortedTimeline, now]);

  const pendingMeetings = useMemo(
    () => (meetings || []).filter(m => m.status === 'pending'),
    [meetings]
  );

  const partnerFor = (m) =>
    (m.requester_id === user?.user_id
      ? { user_id: m.recipient_id, name: m.recipient_name }
      : { user_id: m.requester_id, name: m.requester_name });

  const handleAction = async (requestId, status) => {
    try {
      await onMeetingAction?.(requestId, status, event?.event_id);
      await reload(event?.event_id);
    } catch { /* toast already handled upstream */ }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="my-event-sheet"
        className="w-[95vw] max-w-lg max-h-[92vh] overflow-hidden p-0 gap-0"
      >
        {/* Header — brand gradient so this feels distinct from the admin
            dialogs and signals "yours for the day" at a glance. */}
        <DialogHeader className="px-4 py-3 bg-gradient-to-r from-brand-navy to-brand-sage text-white rounded-t-lg">
          <DialogTitle className="text-base md:text-lg text-white pr-6 flex items-center gap-2">
            <CalendarCheck className="h-5 w-5" /> My Event
          </DialogTitle>
          <DialogDescription className="text-white/80 text-xs">
            {event?.title}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(92vh-4rem)] px-4 py-4 space-y-4">
          {/* Next-up prominent tile */}
          {nextUp && (
            <button
              type="button"
              onClick={() => {
                if (nextUp.kind === 'meeting' && nextUp.with_user_id) {
                  onOpenProfile?.({ user_id: nextUp.with_user_id, name: nextUp.with_user_name });
                }
              }}
              disabled={nextUp.kind !== 'meeting'}
              data-testid="my-event-next-up"
              className={`w-full text-left rounded-xl border-2 border-brand-sage/40 bg-gradient-to-br from-brand-sage/10 to-brand-navy/5 p-3.5 ${
                nextUp.kind === 'meeting' ? 'hover:border-brand-sage transition-colors cursor-pointer' : ''
              }`}
            >
              <p className="text-[10px] font-bold text-brand-sage uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
                <Sparkles className="h-3 w-3" /> Up next
              </p>
              <p className="font-heading font-semibold text-slate-800 text-base leading-snug" data-testid="my-event-next-up-title">
                {nextUp.title}
              </p>
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 text-slate-400" />
                  <strong className="font-mono">{nextUp.start_time}{nextUp.end_time ? `–${nextUp.end_time}` : ''}</strong>
                </span>
                {nextUp.location && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 text-slate-400" />
                    {nextUp.location}
                  </span>
                )}
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                  nextUp.kind === 'meeting'
                    ? 'bg-brand-sage/15 text-brand-sage'
                    : 'bg-slate-200 text-slate-600'
                }`}>
                  {nextUp.kind === 'meeting' ? 'Meeting' : 'Session'}
                </span>
              </div>
            </button>
          )}

          {/* Quick actions — the three fast lanes into the surrounding features */}
          <div className="grid grid-cols-3 gap-2" data-testid="my-event-quick-actions">
            <button
              type="button"
              onClick={() => { onClose(); onOpenAttendees?.(); }}
              data-testid="my-event-action-attendees"
              className="rounded-lg border border-slate-200 bg-white hover:bg-slate-50 p-3 flex flex-col items-center gap-1 transition-colors"
            >
              <Users className="h-5 w-5 text-brand-navy" />
              <span className="text-xs font-medium text-slate-700">Attendees</span>
            </button>
            <button
              type="button"
              onClick={() => { onClose(); onOpenMessages?.(); }}
              data-testid="my-event-action-messages"
              className="rounded-lg border border-slate-200 bg-white hover:bg-slate-50 p-3 flex flex-col items-center gap-1 transition-colors"
            >
              <MessageSquare className="h-5 w-5 text-brand-navy" />
              <span className="text-xs font-medium text-slate-700">Messages</span>
            </button>
            <button
              type="button"
              onClick={() => { onClose(); onOpenAvailability?.(); }}
              data-testid="my-event-action-availability"
              className="rounded-lg border border-slate-200 bg-white hover:bg-slate-50 p-3 flex flex-col items-center gap-1 transition-colors"
            >
              <CalendarPlus className="h-5 w-5 text-brand-navy" />
              <span className="text-xs font-medium text-slate-700">Availability</span>
            </button>
          </div>

          {/* Pending meeting invites — surfaced here as actionable rows */}
          {pendingMeetings.length > 0 && (
            <section data-testid="my-event-pending-section">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                <CalendarPlus className="h-3.5 w-3.5" /> Pending meeting requests
              </p>
              <div className="space-y-1.5">
                {pendingMeetings.map(m => {
                  const partner = partnerFor(m);
                  const isInbound = m.recipient_id === user?.user_id;
                  return (
                    <div
                      key={m.request_id}
                      data-testid={`my-event-pending-${m.request_id}`}
                      className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs"
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full flex-shrink-0 mt-0.5">
                          {isInbound ? 'Invite' : 'Awaiting reply'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-800 truncate">
                            {isInbound ? `From ${m.requester_name}` : `To ${m.recipient_name}`}
                          </p>
                          <p className="text-slate-600 font-mono text-[11px]">
                            {m.proposed_date} · {m.start_time}–{m.end_time}
                            {m.meeting_point && <span className="text-slate-400"> · {m.meeting_point}</span>}
                          </p>
                          {m.message && (
                            <p className="text-slate-500 italic mt-1 text-[11px] break-words">"{m.message}"</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1.5 mt-2 flex-wrap">
                        {isInbound && (
                          <>
                            <Button size="sm" onClick={() => handleAction(m.request_id, 'accepted')}
                              className="h-7 text-xs px-2.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid={`my-event-accept-${m.request_id}`}>
                              Accept
                            </Button>
                            <Button size="sm" variant="outline"
                              onClick={() => handleAction(m.request_id, 'declined')}
                              className="h-7 text-xs px-2.5"
                              data-testid={`my-event-decline-${m.request_id}`}>
                              Decline
                            </Button>
                          </>
                        )}
                        {!isInbound && (
                          <Button size="sm" variant="outline"
                            onClick={() => handleAction(m.request_id, 'cancelled')}
                            className="h-7 text-xs px-2.5"
                            data-testid={`my-event-cancel-${m.request_id}`}>
                            Cancel
                          </Button>
                        )}
                        <Button size="sm" variant="ghost"
                          onClick={() => onOpenProfile?.(partner)}
                          className="h-7 text-xs px-2 text-slate-600"
                          data-testid={`my-event-profile-${m.request_id}`}>
                          <UserIcon className="h-3 w-3 mr-1" /> Profile
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Chronological timeline — sessions + accepted meetings merged */}
          <section data-testid="my-event-timeline">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5" /> Your schedule
            </p>
            {loading ? (
              <p className="text-xs text-slate-400 italic py-4 text-center">Loading…</p>
            ) : sortedTimeline.length === 0 ? (
              <div className="text-xs text-slate-400 italic py-4 text-center" data-testid="my-event-timeline-empty">
                <p>No sessions or meetings scheduled yet.</p>
                <a href={`/events/${event?.event_id}/agenda`} onClick={() => onClose()}
                  data-testid="my-event-open-agenda-link"
                  className="not-italic inline-block mt-2 text-brand-navy font-semibold hover:underline">
                  Star sessions on the agenda
                </a>
              </div>
            ) : (
              <ol className="space-y-1.5">
                {sortedTimeline.map((item, i) => {
                  const isMeeting = item.kind === 'meeting';
                  return (
                    <li
                      key={isMeeting ? item.request_id : `agenda-${i}`}
                      data-testid={`my-event-row-${isMeeting ? 'meeting-' + item.request_id : 'session-' + i}`}
                      className={`rounded-lg border p-2.5 ${
                        isMeeting
                          ? 'border-brand-sage/40 bg-brand-sage/5'
                          : 'border-slate-200 bg-white'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-14 text-xs font-mono text-slate-500 pt-0.5">
                          {item.start_time || '--:--'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">
                            {item.title}
                          </p>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500 mt-0.5">
                            <span className={`text-[10px] font-semibold px-1.5 rounded-full ${
                              isMeeting ? 'bg-brand-sage/15 text-brand-sage' : 'bg-slate-200 text-slate-600'
                            }`}>
                              {isMeeting ? 'Meeting' : 'Session'}
                            </span>
                            {item.end_time && <span className="font-mono">ends {item.end_time}</span>}
                            {item.location && (
                              <span className="inline-flex items-center gap-0.5">
                                <MapPin className="h-3 w-3" /> {item.location}
                              </span>
                            )}
                          </div>
                        </div>
                        {isMeeting && item.with_user_id && (
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => onOpenProfile?.({ user_id: item.with_user_id, name: item.with_user_name })}
                              data-testid={`my-event-meeting-profile-${item.request_id}`}
                              className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-brand-navy transition-colors"
                              title={`View ${item.with_user_name}'s profile`}
                            >
                              <UserIcon className="h-4 w-4" />
                            </button>
                            <a
                              href={`/messages?to=${item.with_user_id}&event_id=${event?.event_id}`}
                              data-testid={`my-event-meeting-message-${item.request_id}`}
                              className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500 hover:text-brand-navy transition-colors"
                              title={`Message ${item.with_user_name}`}
                              onClick={() => onClose()}
                            >
                              <MessageSquare className="h-4 w-4" />
                            </a>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
